import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Settings, Search, Check, X, UserCheck, UserMinus, Plus, Clock, Users, Calendar, Ban, RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import { useToast } from '../../../context/ToastContext';

export default function ReservationsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ today: 0, pending: 0, seated: 0, cancelled: 0 });

  // Custom Cancellation modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [cancelReasonInput, setCancelReasonInput] = useState('');

  useEffect(() => {
    fetchReservations();
  }, [statusFilter, dateFilter]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (statusFilter) queryParams.append('status', statusFilter);
      if (dateFilter) queryParams.append('date', dateFilter);
      if (searchQuery) queryParams.append('search', searchQuery);

      const response = await api.get(`/api/reservations?${queryParams.toString()}`);
      setReservations(response.data || []);

      // Calculate stats based on target date
      const allRes = response.data || [];
      const calculatedStats = allRes.reduce((acc, res) => {
        acc.today++;
        if (res.status === 'pending') acc.pending++;
        if (res.status === 'seated') acc.seated++;
        if (res.status === 'cancelled') acc.cancelled++;
        return acc;
      }, { today: 0, pending: 0, seated: 0, cancelled: 0 });

      setStats(calculatedStats);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchReservations();
  };

  const updateReservationStatus = async (id, newStatus, reason = '') => {
    try {
      await api.put(`/api/reservations/${id}/status`, { status: newStatus, cancellationReason: reason });
      toast.success(`Reservation status updated to ${newStatus}`);
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update reservation status');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'seated': return 'bg-green-50 text-green-700 border-green-100';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-100';
      case 'no_show': return 'bg-gray-50 text-gray-700 border-gray-100';
      default: return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmed';
      case 'seated': return 'Seated';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      case 'no_show': return 'No Show';
      default: return status;
    }
  };

  return (
    <div className="space-y-8 p-6 font-sans">
      
      {/* Header block */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="text-primary-600" />
            Table Reservations
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage and track guest table bookings for your restaurant.</p>
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button 
            onClick={() => navigate('/admin/reservations/settings')}
            className="flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-xl text-sm font-semibold transition-all shadow-sm active:scale-95"
          >
            <Settings size={16} />
            Booking Settings
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Bookings', value: stats.today, icon: Calendar, color: 'from-blue-500 to-indigo-600' },
          { label: 'Pending Bookings', value: stats.pending, icon: Clock, color: 'from-amber-500 to-orange-600' },
          { label: 'Seated Guests', value: stats.seated, icon: Users, color: 'from-green-500 to-emerald-600' },
          { label: 'Cancelled Bookings', value: stats.cancelled, icon: Ban, color: 'from-red-500 to-rose-600' },
        ].map((s, idx) => (
          <div key={idx} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{s.label}</span>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{s.value}</h3>
            </div>
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-tr ${s.color} text-white flex items-center justify-center shadow-sm`}>
              <s.icon size={20} />
            </div>
          </div>
        ))}
      </div>

      {/* Filter and Search controls */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Date select */}
          <input 
            type="date" 
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-700 bg-gray-50/50"
          />

          {/* Status select */}
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-700 bg-white"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="seated">Seated</option>
            <option value="no_show">No Show</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Refresh Button */}
          <button 
            onClick={fetchReservations}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all shadow-sm active:scale-95 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="relative w-full md:w-80">
          <input 
            type="text" 
            placeholder="Search code, name, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <Search className="absolute left-3.5 top-2.5 text-gray-400" size={16} />
          <button type="submit" className="hidden">Submit</button>
        </form>
      </div>

      {/* List Container */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <span className="text-sm font-medium">Fetching bookings...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/55">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Code</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Guest Details</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Time & Date</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Guests</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Status</th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-500 uppercase tracking-wider text-xs">Requests / Notes</th>
                  <th className="px-6 py-4 text-right font-semibold text-gray-500 uppercase tracking-wider text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-55 bg-white">
                {reservations.map((res) => (
                  <tr key={res.id} className="hover:bg-gray-50/30 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900">{res.reservation_code}</td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{res.customer_name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{res.customer_phone}</div>
                      <div className="text-xs text-gray-400">{res.customer_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      <div>{new Date(res.reservation_date).toLocaleDateString()}</div>
                      <div className="text-xs text-gray-400 mt-0.5 font-medium">{res.reservation_time.substring(0, 5)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-800">{res.num_guests} guests</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 border rounded-full text-xs font-semibold ${getStatusBadgeClass(res.status)}`}>
                        {getStatusText(res.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 max-w-xs truncate text-gray-500">
                      {res.special_requests || <span className="text-gray-300 italic">None</span>}
                      {res.cancellation_reason && (
                        <div className="text-xs text-red-500 font-medium mt-1">Reason: {res.cancellation_reason}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1.5">
                        {res.status === 'pending' && (
                          <button
                            onClick={() => updateReservationStatus(res.id, 'confirmed')}
                            className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            title="Confirm Booking"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {res.status === 'confirmed' && (
                          <button
                            onClick={() => updateReservationStatus(res.id, 'seated')}
                            className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                            title="Seat Guests"
                          >
                            <UserCheck size={16} />
                          </button>
                        )}
                        {['pending', 'confirmed'].includes(res.status) && (
                          <>
                            <button
                              onClick={() => {
                                setCancelTargetId(res.id);
                                setCancelReasonInput('');
                                setShowCancelModal(true);
                              }}
                              className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                              title="Cancel Booking"
                            >
                              <X size={16} />
                            </button>
                            <button
                              onClick={() => updateReservationStatus(res.id, 'no_show')}
                              className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                              title="Mark as No Show"
                            >
                              <UserMinus size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {reservations.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <CalendarDays size={48} className="text-gray-200 mb-4" />
                        <p className="text-base font-semibold text-gray-800">No reservations found</p>
                        <p className="text-xs text-gray-400 mt-1">There are no bookings matching your current filter criteria.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cancellation Reason Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-slate-100 shadow-2xl relative font-sans animate-in zoom-in-95 duration-150">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Reservation</h3>
            <p className="text-xs text-gray-500 mb-4">Please provide a reason for cancelling this table booking (optional).</p>
            
            <textarea
              rows="3"
              placeholder="e.g. Guest requested cancellation / Kitchen capacity issue..."
              value={cancelReasonInput}
              onChange={(e) => setCancelReasonInput(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm font-medium outline-none focus:ring-2 focus:ring-primary-500 mb-4"
            />
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="w-1/2 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all active:scale-95"
              >
                Back
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowCancelModal(false);
                  await updateReservationStatus(cancelTargetId, 'cancelled', cancelReasonInput);
                }}
                className="w-1/2 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                Cancel Booking
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
